import React, { useEffect, useMemo, useState } from 'react';
import { getHeaders, openOutreachCase } from '../api';
import { welcomeDraft } from '../lib/welcomeDraft';
import { reachOf, contactIdOf } from '../lib/followUpReach';

// Today rail — the staff follow-up queue on the Dashboard.
//
// squish-backend writes one Salesforce Task per "someone should call this
// person" event (verified signup, web sales inquiry, callback request); this
// rail is where those land: Overdue / Due today / Coming up, a `new` chip for
// anything that arrived in the last 24h, one-click Done and Snooze, and a
// quick-add for manual reminders. Marks are durable by construction — Done is
// Status=Completed and Snooze is an ActivityDate bump on the Task itself, so
// tomorrow's rail honours today's marks with no extra state.
// Design: squish-docs/architecture/follow-up-tasks.md.

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

const DAY_MS = 24 * 60 * 60 * 1000;

function fmtDue(iso, today) {
  if (!iso) return 'no date';
  if (iso === today) return 'today';
  const [y, m, d] = iso.split('-').map(Number);
  const [ty, tm, td] = today.split('-').map(Number);
  const diff = Math.round((Date.UTC(y, m - 1, d) - Date.UTC(ty, tm - 1, td)) / DAY_MS);
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function isNew(createdIso) {
  const t = Date.parse(createdIso || '');
  return t && (Date.now() - t) < DAY_MS;
}

// What the row is "about", for the meta line: the customer when we know
// them, else the Case number (a prospect's sales inquiry has no Account yet).
function aboutOf(t) {
  if (t.Account?.Name) return t.Account.Name;
  if (t.Who?.Name) return t.Who.Name;
  if (t.What?.Type === 'Case' && t.What?.Name) return `#${t.What.Name}`;
  return t.What?.Name || null;
}

function kindOf(subject) {
  const s = String(subject || '');
  if (s.startsWith('Welcome call:')) return 'signup';
  if (s.startsWith('Sales follow-up:')) return 'sales';
  if (s.startsWith('Callback:')) return 'callback';
  return 'manual';
}

const KIND_LABEL = { signup: 'new account', sales: 'sales', callback: 'callback', manual: 'reminder' };

const KIND_PREFIX = { signup: 'Welcome call:', sales: 'Sales follow-up:', callback: 'Callback:' };

// The kind already rides in the meta line, so the row title drops the
// "Welcome call:" / "Sales follow-up:" prefix — 300px is narrow, and the
// prefix was the part every row shared.
function displaySubject(subject, kind) {
  const s = String(subject || '');
  const prefix = KIND_PREFIX[kind];
  return prefix && s.startsWith(prefix) ? s.slice(prefix.length).trim() || s : s;
}

function bucket(rows, today) {
  const overdue = [], dueToday = [], upcoming = [];
  for (const t of rows) {
    const due = t.ActivityDate || null;
    if (due && due < today) overdue.push(t);
    else if (due === today) dueToday.push(t);
    else upcoming.push(t);
  }
  return { overdue, dueToday, upcoming };
}

function Row({ t, today, reach, onOpen, onDone, onSnooze, onWrite, busy }) {
  const [menu, setMenu] = useState(false);
  const kind = kindOf(t.Subject);
  const title = displaySubject(t.Subject, kind);
  const aboutRaw = aboutOf(t);
  const about = aboutRaw && aboutRaw !== title ? aboutRaw : null; // don't say "Jane Doe · Jane Doe"
  const overdue = t.ActivityDate && t.ActivityDate < today;
  const how = reachOf(t, reach);
  // The follow-up says "Welcome call", but most accounts never gave us a
  // number. Rather than leave the rep to find that out by opening the account,
  // the row says it — and offers the thing that CAN be done instead.
  const emailOnly = how.known && !how.phone && !!how.email;
  // A case with no Contact on it has no customer tab in the composer — the
  // reply the button exists to send would be greyed out on arrival. Task.WhoId
  // is polymorphic, so a Lead-anchored follow-up is excluded by the 003 prefix
  // rather than by finding out at create time.
  const canWrite = !!t.AccountId && !!contactIdOf(t) && (!how.known || !!how.email);
  return (
    <div className={`today-row${busy ? ' is-busy' : ''}`}>
      <div className="today-row-main" onClick={() => onOpen(t)} title={t.Description ? t.Description.split('\n').slice(0, 6).join('\n') : undefined}>
        <div className="today-row-subject">
          {t.Priority === 'High' && <span className="today-dot" title="High priority" />}
          <span className="today-row-text">{title || '—'}</span>
          {isNew(t.CreatedDate) && <span className="today-chip today-chip-new">new</span>}
        </div>
        {/* Due date first: the meta line ellipsizes from the right, and the
            date is the one field the eye scans for. */}
        <div className="today-row-meta">
          <span className={overdue ? 'today-due today-due-late' : 'today-due'}>{fmtDue(t.ActivityDate, today)}</span>
          <span className="today-sep">·</span>
          <span className="today-kind">{KIND_LABEL[kind]}</span>
          {/* Only the ABSENCE of a number is worth a word here. Printing the
              number itself pushed the customer's name off the end of a 192px
              line to say something the account page already says. */}
          {emailOnly && <><span className="today-sep">·</span><span className="today-reach">email only</span></>}
          {about && <><span className="today-sep">·</span><span className="today-about">{about}</span></>}
        </div>
      </div>
      <div className="today-row-actions">
        {canWrite && (
          <button data-write
            className="today-btn"
            onClick={() => onWrite(t)}
            disabled={busy}
            title={emailOnly ? 'No number on the account — write to them instead' : 'Write to them'}
          >✉</button>
        )}
        <button data-write className="today-btn" onClick={() => onDone(t)} disabled={busy} title="Mark done">✓</button>
        <div className="today-snooze">
          <button data-write className="today-btn" onClick={() => setMenu(m => !m)} disabled={busy} title="Snooze">⏱</button>
          {menu && (
            <div className="today-menu" onMouseLeave={() => setMenu(false)}>
              {[[1, 'tomorrow'], [3, '3 days'], [7, 'next week']].map(([d, label]) => (
                <button key={d} className="today-menu-item" onClick={() => { setMenu(false); onSnooze(t, d); }}>{label}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, tone, rows, busyIds, ...rest }) {
  if (!rows.length) return null;
  return (
    <div className="today-section">
      <div className={`today-section-head today-tone-${tone}`}>
        <span>{title}</span>
        <span className="today-section-count">{rows.length}</span>
      </div>
      {rows.map(t => <Row key={t.Id} t={t} busy={busyIds.has(t.Id)} {...rest} />)}
    </div>
  );
}

export default function TodayRail({ followUps, staffName, onPickCustomer, onNavigateToTicket }) {
  const today = followUps?.today || new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState(followUps?.open || []);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [saving, setSaving] = useState(false);

  // Re-sync when the dashboard refetches (a stage change, a manual refresh).
  useEffect(() => { setRows(followUps?.open || []); }, [followUps]);

  const { overdue, dueToday, upcoming } = useMemo(() => bucket(rows, today), [rows, today]);
  const newCount = useMemo(() => rows.filter(t => isNew(t.CreatedDate)).length, [rows]);

  function mark(id, on) {
    setBusyIds(prev => { const n = new Set(prev); on ? n.add(id) : n.delete(id); return n; });
  }

  async function patch(t, body, apply) {
    setError('');
    mark(t.Id, true);
    const before = rows;
    setRows(apply(rows)); // optimistic
    try {
      const res = await fetch(`${API}/api/tasks/${t.Id}`, {
        method: 'PATCH', headers: { ...getHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      // No dashboard refetch: the optimistic state IS the new truth once the
      // PATCH succeeded, and a full /api/dashboard reload is a dozen SOQL
      // queries per click.
    } catch (e) {
      setRows(before);
      setError(e.message);
    } finally {
      mark(t.Id, false);
    }
  }

  const onDone = (t) => patch(t, { action: 'complete' }, list => list.filter(x => x.Id !== t.Id));
  const onSnooze = (t, days) => patch(t, { action: 'snooze', days }, list => {
    // Optimistic date: server recomputes from Pacific today; this only has to
    // move the row out of its section until the refetch lands.
    const [y, m, d] = today.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
    return list.map(x => x.Id === t.Id ? { ...x, ActivityDate: next } : x);
  });

  // ✉ — open the conversation and land in it with a draft already written.
  //
  // The draft is seeded into the composer's own localStorage key rather than
  // passed through navigation: the workspace already restores a draft per case
  // on mount, so this needs no new plumbing and survives the rep closing the
  // panel to go look something up. An existing draft is never overwritten —
  // coming back to a half-written hello must not wipe it.
  //
  // Sends nothing. The composer still opens INTERNAL, so reaching the customer
  // is two deliberate acts (flip to customer, pass the review step) exactly as
  // it is for every other reply.
  async function onWrite(t) {
    if (!t.AccountId) return;
    setError('');
    mark(t.Id, true);
    try {
      const out = await openOutreachCase(t.AccountId, { contactId: contactIdOf(t), taskId: t.Id });
      const key = `crm.caseDraft.${out.caseId}`;
      try {
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, welcomeDraft({
            contactName: t.Who?.Name || t.Account?.Name || null,
            staffName,
          }));
        }
      } catch { /* storage unavailable — the rep gets an empty composer, not an error */ }
      // The Task now hangs off the case, so the row's own click target moved
      // with it; reflect that without a full dashboard refetch.
      setRows(list => list.map(x => x.Id === t.Id
        ? { ...x, WhatId: out.caseId, What: { Type: 'Case', Name: out.caseNumber || null } }
        : x));
      onNavigateToTicket?.(out.caseId);
    } catch (e) {
      // 403 is the account-access guard: this account has not been unlocked in
      // this tab. Say so in those words and take the rep to the place the
      // unlock lives, rather than leaving them re-clicking a dead button.
      if (e.status === 403) {
        setError('Open the account first to unlock it, then write from there.');
        onPickCustomer?.({ Id: t.AccountId, Name: t.Account?.Name });
      } else {
        setError(e.message);
      }
    } finally {
      mark(t.Id, false);
    }
  }

  const onOpen = (t) => {
    if (t.What?.Type === 'Case' && t.WhatId) return onNavigateToTicket?.(t.WhatId);
    if (t.AccountId) return onPickCustomer?.({ Id: t.AccountId, Name: t.Account?.Name });
    if (t.What?.Type === 'Account' && t.WhatId) return onPickCustomer?.({ Id: t.WhatId, Name: t.What?.Name });
  };

  async function submitNew(e) {
    e.preventDefault();
    const subject = newSubject.trim();
    if (!subject || saving) return;
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/tasks`, {
        method: 'POST', headers: { ...getHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ subject }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setRows(list => [...list, { Id: data.id || `tmp-${Date.now()}`, Subject: data.task?.Subject || subject, Status: 'Not Started', Priority: 'Normal', ActivityDate: data.task?.ActivityDate || null, CreatedDate: new Date().toISOString() }]);
      setNewSubject(''); setAdding(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const rowProps = { today, reach: followUps?.reach, onOpen, onDone, onSnooze, onWrite, busyIds };

  return (
    <aside className="today-rail">
      <div className="today-head">
        <div>
          <div className="today-title">Today</div>
          <div className="today-subtitle">
            {rows.length} open{newCount ? ` · ${newCount} new` : ''}{overdue.length ? ` · ${overdue.length} overdue` : ''}
          </div>
        </div>
        <button data-write className="today-add" onClick={() => setAdding(a => !a)} title="Add a reminder">+</button>
      </div>

      {adding && (
        <form className="today-addform" onSubmit={submitNew}>
          <input
            autoFocus
            value={newSubject}
            onChange={e => setNewSubject(e.target.value)}
            placeholder="Call Acme re: sizing… (due tomorrow)"
            disabled={saving}
          />
          <button type="submit" disabled={!newSubject.trim() || saving}>{saving ? '…' : 'Add'}</button>
        </form>
      )}

      {error && <div className="today-error">⚠ {error}</div>}

      {followUps?.unavailable ? (
        <div className="today-empty">Tasks unavailable right now — Salesforce read failed.</div>
      ) : rows.length === 0 ? (
        <div className="today-empty">
          <div>✓ Nothing on your plate.</div>
          <div className="today-empty-hint">New accounts, sales inquiries and callback requests land here automatically.</div>
        </div>
      ) : (
        <div className="today-body">
          <Section title="Overdue" tone="late" rows={overdue} {...rowProps} />
          <Section title="Due today" tone="today" rows={dueToday} {...rowProps} />
          <Section title="Coming up" tone="soon" rows={upcoming} {...rowProps} />
        </div>
      )}
    </aside>
  );
}
